'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { createClient } from '@/lib/supabase/client'
import { initializeUserLanguage } from '@/lib/userPreferences'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Logo } from '@/components/Logo'
import { AlertCircle, Globe, ArrowRight, Sparkles } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { locales, type Locale, getLocale, setLocale } from '@/i18n/client'

const languageNames: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  zh: '中文',
  ru: 'Русский',
  ar: 'العربية',
  hi: 'हिन्दी',
  nl: 'Nederlands',
  pl: 'Polski',
  tr: 'Türkçe',
}

export default function LoginPage() {
  const t = useTranslations()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [currentLocale, setCurrentLocale] = useState<Locale>('en')
  const router = useRouter()
  const supabase = createClient()

  useEffect(() => {
    setCurrentLocale(getLocale())
  }, [])

  const handleLanguageChange = (locale: Locale) => {
    setLocale(locale)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      // Initialize/sync user language after successful login
      await initializeUserLanguage()

      // Use window.location.href for full page reload to ensure middleware
      // properly recognizes the new session cookies
      window.location.href = '/app/dashboard'
    } catch (err: any) {
      setError(err.message || t('auth.errors.loginFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md relative z-10">
        {/* Language Selector */}
        <div className="flex justify-end mb-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 rounded-full">
                <Globe className="h-4 w-4" />
                {languageNames[currentLocale]}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[400px] overflow-y-auto">
              {locales.map((locale) => (
                <DropdownMenuItem
                  key={locale}
                  onClick={() => handleLanguageChange(locale)}
                  className={currentLocale === locale ? 'bg-muted text-foreground' : ''}
                >
                  {languageNames[locale]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Logo and Tagline */}
        <div className="text-center mb-10">
          <Link href="/" className="inline-flex justify-center mb-4">
            <Logo size="lg" />
          </Link>
          <p className="text-muted-foreground">{t('auth.tagline')}</p>
        </div>

        {/* Login Card */}
        <Card className="rounded-2xl shadow-float">
          <CardHeader className="text-center pb-6">
            <div className="inline-flex items-center justify-center gap-2 mx-auto mb-4 px-4 py-1.5 rounded-full bg-speaker-1/10">
              <Sparkles className="h-3.5 w-3.5 text-speaker-1" />
              <span className="text-xs font-semibold text-speaker-1 tracking-wide">
                Welcome back
              </span>
            </div>
            <CardTitle className="text-2xl font-semibold">{t('auth.login')}</CardTitle>
            <CardDescription className="text-muted-foreground">{t('auth.loginDescription')}</CardDescription>
          </CardHeader>

          <form onSubmit={handleLogin}>
            <CardContent className="space-y-5">
              {error && (
                <div className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-xl text-sm">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="h-12"
                />
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-5">
              <Button
                type="submit"
                className="w-full group"
                size="lg"
                disabled={loading}
              >
                {loading ? t('common.loading') : t('auth.login')}
                {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
              </Button>

              <div className="text-sm text-center text-muted-foreground">
                {t('auth.noAccount')}{' '}
                <Link href="/signup" className="text-foreground hover:text-speaker-1 font-semibold transition-colors">
                  {t('auth.signup')}
                </Link>
              </div>
            </CardFooter>
          </form>
        </Card>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
            ← {t('common.backToHome', { defaultValue: 'Back to home' })}
          </Link>
        </div>
      </div>
    </div>
  )
}
